/**
 * @fileoverview ScreenAnimationProvider — resolves the screen's animation plan
 * once and gates when in-screen animations are allowed to run.
 * @module screen-view-v2/animations/AnimationContext
 *
 * Gating (`enabled`):
 *   - `mounted` flips in a LAYOUT effect → the server render carries NO
 *     animation classes (SSR HTML never bakes `opacity:0`), while on the
 *     client the classes attach in a re-render committed BEFORE the browser
 *     paints — the first painted frame of a mounting screen already shows
 *     pending entrances at their `from` state (no final-layout flash).
 *     web-shell uses createRoot().render(), never hydrateRoot, so there is
 *     no hydration-mismatch concern; the isomorphic fallback below only
 *     silences React's useLayoutEffect warning during renderToString.
 *   - `previewMode === 'static'` (canvas thumbnails) never animates.
 *   - `previewMode === 'edit'` animates only while the editor Play control is
 *     active (`isAnimationPlaying`); a rising edge remounts the subtree via a
 *     Fragment key so CSS animations restart (re-adding an identical class
 *     never restarts a CSS animation).
 *   - `prefers-reduced-motion` disables everything (JS half of the double
 *     guard; element-animations.css carries the CSS half).
 *   - ssrEntrance (FSE): the server render of the static first screen keeps its entrances, running
 *
 * `screenEntered` (threaded from web-shell) holds mount-driven entrances
 * PAUSED at their `from` state (`--sv2-anim-play: paused`) until the screen
 * transition settles — see useElementAnimation.ts.
 *
 * `skipMountEntrances` (threaded from web-shell, AMV-AC-036) marks the screen
 * the static (SSR) page already painted: its mount-driven entrances render
 * their final state, so the client takeover never replays them.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ScreenAnimation, StepContent } from '@segmently/schema-models/v2';
import {
  resolveScreenAnimationPlan,
  OPTIONS_ITEM_REF,
  PAYWALL_PRODUCT_ITEM_REF,
  type ScreenAnimationPlan,
} from './resolveScreenAnimationPlan';
import { buildElementAnimationSpec } from './elementAnimationSpec';
import type { PreviewMode } from '../types/previewMode';

// useLayoutEffect on the client (commits before paint — no flash of the final
// state); useEffect during the builder's renderToString pass, purely to avoid
// React's "useLayoutEffect does nothing on the server" warning.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// ============================================================================
// Context
// ============================================================================

export interface ScreenAnimationContextValue {
  plan: ScreenAnimationPlan;
  /** Master gate — when false every animation hook returns {} */
  enabled: boolean;
  /**
   * Gate for EVENT-driven feedback (selection, tap): the master gate minus
   * the edit-mode play-window condition. In live mode it equals `enabled`;
   * in the editor, user-initiated feedback stays previewable after the
   * entrance window closes (clicking an option must always respond).
   */
  eventsEnabled: boolean;
  /** Gate for onScreenEnter-triggered entries (true when no transition pending) */
  screenEntered: boolean;
  /** The static page already painted this screen: mount-driven entrances render final (loops exempt) */
  skipMountEntrances: boolean;
  /** The published render of the static page's first screen: only entrances render, running from first paint (FSE) */
  ssrEntranceOnly: boolean;
  reducedMotion: boolean;
}

const ScreenAnimationContext = createContext<ScreenAnimationContextValue | null>(null);

export function useScreenAnimationContext(): ScreenAnimationContextValue | null {
  return useContext(ScreenAnimationContext);
}

// ============================================================================
// Provider
// ============================================================================

/** Screens whose timer components own the editor Play completion callback */
const PLAYBACK_OWNING_SCREEN_TYPES = new Set(['Carousel', 'ProgressSteps']);

const MAX_COMPLETION_WAIT_MS = 15_000;

function prefersReducedMotionSafe(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface ScreenAnimationProviderProps {
  content: StepContent | null | undefined;
  screenType: string;
  /** The render locale: a slot takes a stagger step only if its text / image resolves for it (D-F14d) */
  locale?: string;
  defaultScreenAnimation?: ScreenAnimation | null;
  previewMode: PreviewMode;
  ssrMode?: boolean;
  isAnimationPlaying?: boolean;
  onAnimationComplete?: () => void;
  /**
   * Entrance-only completion for a Play session (edit mode): fires once after the longest
   * finite entrance ends — immediately when nothing will play — for EVERY screen type.
   * Unlike onAnimationComplete, a Carousel/ProgressSteps screen does not hold it for its
   * slide/step cycle (the theme gallery's one-shot Play ends with the entrances).
   */
  onEntranceComplete?: () => void;
  /**
   * False while a screen transition is still running (web-shell threads this).
   * Undefined (editor) means "entered" — entrance runs on mount/replay.
   */
  screenEntered?: boolean;
  /**
   * True on the screen the static (SSR) page painted, for the client mount that
   * replaces it (web-shell threads this). Its mount-driven entrances render their
   * final state — the painted content never drops to its `from` state and plays
   * again (AMV-AC-036). Loops, tap and selection feedback are unaffected.
   */
  skipMountEntrances?: boolean;
  /**
   * True for the screen the published render (ssrMode) paints as the static page's first screen (web-shell threads
   * it from the page profile). Its entrance-category, mount-driven animations render running from first paint;
   * the published page's <head> class switches them on (FSE). Needs ssrMode and previewMode 'live'.
   */
  ssrEntrance?: boolean;
  children: React.ReactNode;
}

export function ScreenAnimationProvider({
  content,
  screenType,
  locale,
  defaultScreenAnimation,
  previewMode,
  ssrMode = false,
  isAnimationPlaying = false,
  onAnimationComplete,
  onEntranceComplete,
  screenEntered,
  skipMountEntrances = false,
  ssrEntrance = false,
  children,
}: ScreenAnimationProviderProps): React.ReactElement {
  // SSR safety: the server render carries no animation classes (FSE's
  // `ssrEntranceOnly` first-screen entrance aside, see below). On the client
  // the layout effect commits the mounted=true re-render BEFORE paint, so the
  // first painted frame already carries (paused) animation classes — elements
  // never flash their final state. Same-value set is a no-op —
  // StrictMode-idempotent.
  const [mounted, setMounted] = useState(false);
  useIsomorphicLayoutEffect(() => {
    setMounted(true);
  }, []);

  const resolved = useMemo(
    () => resolveScreenAnimationPlan(content, defaultScreenAnimation, screenType, locale),
    [content, defaultScreenAnimation, screenType, locale],
  );

  const reducedMotion = mounted ? prefersReducedMotionSafe() : false;

  // NOTE: not gated on plan.size — TapTargetStyle button animations bypass
  // the plan (Button reads its own appearance) but still respect `enabled`.
  // FSE (B-wait): the published render paints the first screen's entrance itself (no `mounted` there).
  const ssrEntranceOnly = ssrMode && ssrEntrance && previewMode === 'live';
  const eventsEnabled =
    mounted &&
    !ssrMode &&
    !reducedMotion &&
    previewMode !== 'static';
  const enabled = ssrEntranceOnly || (eventsEnabled && (previewMode !== 'edit' || isAnimationPlaying));

  // Edit-mode replay: rising edge of the Play control bumps the nonce, and the
  // Fragment key remount restarts all CSS animations from their first frame.
  const [replayNonce, setReplayNonce] = useState(0);
  const wasPlayingRef = useRef(isAnimationPlaying);
  useEffect(() => {
    if (previewMode === 'edit' && isAnimationPlaying && !wasPlayingRef.current
      && resolved.hasEntranceEntries) {
      setReplayNonce((n) => n + 1);
    }
    wasPlayingRef.current = isAnimationPlaying;
  }, [isAnimationPlaying, previewMode, resolved.hasEntranceEntries]);

  // Editor Play completion: fire onAnimationComplete after the longest finite
  // entrance ends — but only when no timer component (Carousel/ProgressSteps)
  // owns that callback already. A play session with a callback ALWAYS
  // resolves, even when nothing animates (see waitMs below).
  const completionMs = useMemo(() => {
    if (!resolved.hasEntranceEntries) return 0;
    let max = 0;
    for (const [ref, entry] of resolved.plan) {
      if (ref.includes('#')) continue;
      // Item templates animate one element per item — the LAST item's stagger
      // delay bounds the total entrance duration.
      const staggerIndex = ref === OPTIONS_ITEM_REF
        ? Math.max(resolved.optionsItemCount - 1, 0)
        : ref === PAYWALL_PRODUCT_ITEM_REF
          ? Math.max(resolved.productItemCount - 1, 0)
          : entry.staggerIndex ?? 0;
      const spec = buildElementAnimationSpec(entry.animation, {
        reducedMotion: false,
        staggerIndex,
        stagger: entry.stagger,
        defaultTrigger: entry.defaultTrigger,
        forceLoop: entry.forceLoop,
      });
      if (!spec || spec.iterationCount === 'infinite') continue;
      const total = spec.delayMs + spec.durationMs * Number(spec.iterationCount);
      if (total > max) max = total;
    }
    return Math.min(max, MAX_COMPLETION_WAIT_MS);
  }, [resolved]);

  useEffect(() => {
    if (
      previewMode !== 'edit' ||
      !isAnimationPlaying ||
      !mounted ||
      !onAnimationComplete ||
      PLAYBACK_OWNING_SCREEN_TYPES.has(screenType)
    ) {
      return;
    }
    // Entrances playing → wait out the longest one. Nothing will visibly play
    // (no entrance entries, or master gate off e.g. reduced motion) → resolve
    // the session immediately so host Play buttons never stick in "playing".
    const waitMs = enabled && completionMs > 0 ? completionMs : 0;
    const timer = setTimeout(() => onAnimationComplete(), waitMs);
    return () => clearTimeout(timer);
    // replayNonce restarts the timer on every replay
  }, [previewMode, isAnimationPlaying, mounted, enabled, completionMs, onAnimationComplete, screenType, replayNonce]);

  // Entrance-only completion: the same wait, for every screen type (no timer-component carve-out).
  useEffect(() => {
    if (previewMode !== 'edit' || !isAnimationPlaying || !mounted || !onEntranceComplete) return;
    const waitMs = enabled && completionMs > 0 ? completionMs : 0;
    const timer = setTimeout(() => onEntranceComplete(), waitMs);
    return () => clearTimeout(timer);
  }, [previewMode, isAnimationPlaying, mounted, enabled, completionMs, onEntranceComplete, replayNonce]);

  const value = useMemo<ScreenAnimationContextValue>(
    () => ({
      plan: resolved.plan,
      enabled,
      eventsEnabled,
      screenEntered: screenEntered !== false,
      skipMountEntrances,
      ssrEntranceOnly,
      reducedMotion,
    }),
    [resolved.plan, enabled, eventsEnabled, screenEntered, skipMountEntrances, ssrEntranceOnly, reducedMotion],
  );

  return (
    <ScreenAnimationContext.Provider value={value}>
      <React.Fragment key={replayNonce}>{children}</React.Fragment>
    </ScreenAnimationContext.Provider>
  );
}

/**
 * Scope for content the static page never painted as it is shown now — a Carousel slide that was
 * `display:none` in the static page, or one hidden since (F14 / AMV-AC-037): the takeover skip
 * (`skipMountEntrances`, AMV-AC-036) does not apply inside it, because showing it is its entrance,
 * not a replay. Renders no DOM (the static page stays byte-identical); outside a painted screen it
 * passes the context through unchanged.
 */
export function UnpaintedContentScope({
  unpainted,
  children,
}: {
  unpainted: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const parent = useContext(ScreenAnimationContext);
  const value = useMemo(
    () => (parent && unpainted && parent.skipMountEntrances ? { ...parent, skipMountEntrances: false } : parent),
    [parent, unpainted],
  );
  return <ScreenAnimationContext.Provider value={value}>{children}</ScreenAnimationContext.Provider>;
}
